import React, { useEffect, useState } from "react";
import { fetchAllCongressmen } from "services/services.congressman";
import {
  fetchAllAgencies,
  fetchAllAgencyProjects,
} from "services/services.projects";
import {
  fetchOthersList,
  fetchUtilizationReports,
} from "services/services.utilization";
import { canAccessPage, canEditRoles, congRoles } from "utility/constants";
import { getFormattedOptions, showDeleteModal } from "utility/helpers";
import { Col } from "reactstrap";
import SettingsModal from "components/customs/settings-modal";
import { useNavigate } from "react-router";
import "react-datepicker/dist/react-datepicker.css";
import ImageVideoSlider from "components/customs/image-video-slider";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import UtilizationStats from "./UtilizationStats";
import { PRINCIPAL_IDS } from "./constants";
import EventReports from "./EventReports";
import UtilizationReportsFilters from "./Filters";

const MainContainer = styled.div`
  margin-top: 50px;
`;

const HeaderTextSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const UtilizationView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPrincipal, setSelectedPrincipal] = useState(null);
  const [selectedOthers, setSelectedOthers] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [congressmenOptions, setCongressmenOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [othersOptions, setOthersOptions] = useState([]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [images, setImages] = useState([]);
  const [displayOthersFilter, setDisplayOthersFilter] = useState(false);
  const [disablePrincipal, setDisablePrincipal] = useState(false);
  const [utilizationReports, setUtilizationReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [totalLiquidation, setTotalLiquidation] = useState(0);
  const [unliquidatedBalance, setUnliquidatedBalance] = useState(0);
  const [noOfReports, setNoOfReports] = useState(0);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isAllAgency, setIsAllAgency] = useState(true);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState({});

  let changeSearchTimeoutID;
  const role = user?.type;

  const toggleModal = () => setIsModalShown(!isModalShown);

  const toggleCarousel = () => setShowOverlay(!showOverlay);

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        let newData = getFormattedOptions("name", data);

        if (canEditRoles.includes(role)) {
          const allOption = { id: 0, label: "All" };
          newData = [allOption, ...newData];
        }

        setAgencyOptions(newData);

        setSelectedAgency(newData[0]);
      },
    });
  };

  const fetchProjects = () => {
    fetchAllAgencyProjects({
      url: `/agency/${selectedAgency?.value}`,
      callback: (data) => {
        let projects = data?.Projects;

        if (selectedAgency?.value === 14) {
          projects = data?.Projects.filter(
            (project) => project.name === "Infrastructure"
          );
        }

        let options = getFormattedOptions("name", projects);

        if (canEditRoles.includes(role)) {
          const allOption = { id: 0, label: "All" };
          options = [allOption, ...options];
        }

        if (selectedAgency?.value === 14) setSelectedProject(options[0]);
        setProjectOptions(options);
        setSelectedProject(options[0]);
      },
    });
  };

  const fetchCongressmen = async () => {
    await fetchAllCongressmen({
      callback: (data) => {
        let newData = getFormattedOptions("name", data);

        if (canEditRoles.includes(role)) {
          const allOption = { id: 0, label: "All" };
          newData = [allOption, ...newData];
        }

        setCongressmenOptions(newData);

        if (canAccessPage.includes(role)) {
          const defaultCong = newData.find((cong) => cong.id === 0);
          setDisplayOthersFilter(true);
          setSelectedPrincipal(defaultCong);
        } else if (congRoles.includes(role)) {
          setDisablePrincipal(true);

          const defaultCong = newData.find(
            (cong) => cong.id === user?.congressman?.id
          );
          setSelectedPrincipal(defaultCong);
        }
      },
    });
  };

  const fetchOthers = (congId) => {
    fetchOthersList({
      url: `/utilization/others/list?congressmanId=${congId}`,

      callback: (data) => {
        const newData = getFormattedOptions("allocationContactName", data);
        setOthersOptions(newData);
      },
    });
  };

  const fetchDataTable = () => {
    const congId = selectedPrincipal?.value;
    const agencyId = selectedAgency?.value;
    const projId = selectedProject?.value;
    const others = selectedOthers?.allocationContactName;

    fetchUtilizationReports({
      url: "/utilization",
      params: {
        congressmanId: congId,
        projectId: projId,
        agencyId,
        principalOthers: others,
        limit: 100,
        offset: 0,
        column: "createdAt",
        order: "DESC",
      },
      callback: (data) => {
        setUtilizationReports(data.rows);
        setTotalAllocation(data.statistics.totalAllocation);
        setTotalLiquidation(data.statistics.totalLiquidation);
        setUnliquidatedBalance(data.statistics.unLiquidated);
        setNoOfReports(data.count);
      },
    });
  };

  const filteredReports = utilizationReports.filter((row) =>
    row.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;
      setSearchQuery(eventValue.trim());
    }, 1000);
  };

  const handleDeleteRow = (row) => {
    showDeleteModal({
      title: row.title,
      url: `utilization/${row.id}`,
      callBack: () => {
        fetchDataTable();
      },
    });
  };

  useEffect(() => {
    //  fetch all agencies & congressmen
    fetchCongressmen();
    fetchAgencies();
    fetchDataTable();
  }, []);

  useEffect(() => {
    if (selectedPrincipal) {
      if (PRINCIPAL_IDS.includes(selectedPrincipal.id)) {
        setDisplayOthersFilter(true);
        fetchOthers(selectedPrincipal.id);
      } else {
        setDisplayOthersFilter(false);
      }
    }
  }, [selectedPrincipal]);

  useEffect(() => {
    if (selectedAgency && selectedAgency?.value !== undefined) {
      // reset selected project on agency change
      fetchProjects();
      setSelectedProject(null);
    }
  }, [selectedAgency]);

  useEffect(() => {
    if (
      (selectedPrincipal && (selectedProject || selectedAgency?.id === 0)) ||
      selectedOthers
    ) {
      fetchDataTable();
    }

    if (role === "Agency" && selectedProject && !selectedPrincipal) {
      fetchDataTable();
    }
  }, [selectedPrincipal, selectedProject, selectedOthers, selectedAgency]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <MainContainer>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{"Utilization Report"}</HeaderText>
        </HeaderTextSection>

        <UtilizationReportsFilters
          congressmenOptions={congressmenOptions}
          selectedPrincipal={selectedPrincipal}
          setSelectedPrincipal={setSelectedPrincipal}
          displayOthersFilter={displayOthersFilter}
          disablePrincipal={disablePrincipal}
          othersOptions={othersOptions}
          selectedOthers={selectedOthers}
          agencyOptions={agencyOptions}
          setSelectedOthers={setSelectedOthers}
          selectedAgency={selectedAgency}
          setSelectedAgency={setSelectedAgency}
          setIsAllAgency={setIsAllAgency}
          setSelectedProject={setSelectedProject}
          isAllAgency={isAllAgency}
          projectOptions={projectOptions}
          selectedProject={selectedProject}
          onChangeSearch={onChangeSearch}
          toggleModal={toggleModal}
        />
        <hr />
        <UtilizationStats
          totalAllocation={totalAllocation}
          totalLiquidation={totalLiquidation}
          unliquidatedBalance={unliquidatedBalance}
          noOfReports={noOfReports}
        />

        <EventReports
          filteredReports={filteredReports}
          setImages={setImages}
          toggleCarousel={toggleCarousel}
          setIsDownloadInProgress={setIsDownloadInProgress}
          isDownloadInProgress={isDownloadInProgress}
          navigate={navigate}
          handleDeleteRow={handleDeleteRow}
        />
      </Col>

      <ImageVideoSlider
        images={images}
        isOpen={showOverlay}
        setIsOpen={setShowOverlay}
      />
      {canEditRoles.includes(role) && (
        <SettingsModal
          isOpen={isModalShown}
          handleClose={toggleModal}
          agency={agencyOptions}
          handleFetchTable={fetchDataTable}
        />
      )}
    </MainContainer>
  );
};

export default UtilizationView;
